import { Card, CardContent, Logo, buttonVariants } from "@exhousing/design-system";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { apiClient } from "@/api/client";

type Status = "verifying" | "success" | "error";

// Module-level guard: survives React StrictMode's dev remount so the token is
// submitted exactly once (a second submit would 404 on the now-consumed token).
const submitted = new Set<string>();

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState<Status>(token ? "verifying" : "error");

  useEffect(() => {
    // Dedup-guard so the token is submitted once (incl. StrictMode's dev double-run).
    // No per-effect cancel flag on purpose: the request result must always land on the
    // live component, even after StrictMode's throwaway cleanup.
    if (!token || submitted.has(token)) return;
    submitted.add(token);
    apiClient
      .POST("/api/v1/auth/verify-email", { body: { token } })
      .then(({ error }) => setStatus(error ? "error" : "success"))
      .catch(() => setStatus("error"));
  }, [token]);

  return (
    <div className="relative flex min-h-full items-center justify-center overflow-hidden px-4 py-12">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-sand-100 via-background to-brand-50" />
      <div className="w-full max-w-sm animate-fade-in-up text-center">
        <Logo showWordmark={false} className="mx-auto mb-6 scale-125" />
        <Card className="shadow-lg">
          <CardContent className="space-y-3 py-10">
            {status === "verifying" && (
              <>
                <Loader2 className="mx-auto size-12 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Verifying your email…</p>
              </>
            )}

            {status === "success" && (
              <>
                <CheckCircle2 className="mx-auto size-12 text-success" />
                <h1 className="font-display text-xl font-medium">Email verified</h1>
                <p className="text-sm text-muted-foreground">
                  Your account is active. You can now sign in.
                </p>
                <Link to="/login" className={buttonVariants({ className: "mt-2" })}>
                  Sign in
                </Link>
              </>
            )}

            {status === "error" && (
              <>
                <XCircle className="mx-auto size-12 text-warning" />
                <h1 className="font-display text-xl font-medium">
                  Link already used or expired
                </h1>
                <p className="text-sm text-muted-foreground">
                  This verification link is no longer valid. If you&apos;ve already
                  verified your email, just sign in.
                </p>
                <Link to="/login" className={buttonVariants({ className: "mt-2" })}>
                  Go to sign in
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
